import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AUTH_TOKEN } from '@shared/constants';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface ImpersonatedUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
  metadata?: any;
}

interface ImpersonatingAdmin {
  id: string;
  email: string;
  name: string;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  impersonatingAdmin: ImpersonatingAdmin | null;
  impersonationToken: string | null;
  expiresAt: string | null;
  startImpersonation: (token: string) => Promise<boolean>;
  endImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const IMPERSONATION_STORAGE_KEY = 'impersonation_data';

interface StoredImpersonationData {
  token: string;
  user: ImpersonatedUser;
  admin: ImpersonatingAdmin;
  expiresAt: string;
}

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);
  const [impersonatingAdmin, setImpersonatingAdmin] = useState<ImpersonatingAdmin | null>(null);
  const [impersonationToken, setImpersonationToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  // End impersonation session
  const endImpersonation = useCallback(() => {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    setIsImpersonating(false);
    setImpersonatedUser(null);
    setImpersonatingAdmin(null);
    setImpersonationToken(null);
    setExpiresAt(null);
  }, []);

  // Start impersonation with a token
  const startImpersonation = useCallback(async (token: string): Promise<boolean> => {
    try {
      // Validate the token with the backend
      const response = await axios.post(`${API_BASE_URL}/api/impersonate/validate`, { token });

      if (!response.data.valid) {
        return false;
      }

      const { user, impersonatedBy, expiresAt: expires } = response.data;

      // Clear any existing Auth0 token to prevent conflicts
      // This ensures impersonation completely overrides any existing login
      localStorage.removeItem(AUTH_TOKEN.PORTAL);

      // Store impersonation data
      const impersonationData: StoredImpersonationData = {
        token,
        user,
        admin: impersonatedBy,
        expiresAt: expires
      };

      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(impersonationData));

      setIsImpersonating(true);
      setImpersonatedUser(user);
      setImpersonatingAdmin(impersonatedBy);
      setImpersonationToken(token);
      setExpiresAt(expires);

      return true;
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      return false;
    }
  }, []);

  // Load impersonation data from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (stored) {
      try {
        const data: StoredImpersonationData = JSON.parse(stored);

        // Check if the session has expired
        if (new Date(data.expiresAt) <= new Date()) {
          endImpersonation();
          return;
        }

        setIsImpersonating(true);
        setImpersonatedUser(data.user);
        setImpersonatingAdmin(data.admin);
        setImpersonationToken(data.token);
        setExpiresAt(data.expiresAt);
      } catch (error) {
        console.error('Failed to parse impersonation data:', error);
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    }
  }, [endImpersonation]);

  // Check for expiration periodically
  useEffect(() => {
    if (!expiresAt) return;

    const checkExpiration = () => {
      if (new Date(expiresAt) <= new Date()) {
        endImpersonation();
      }
    };

    // Check every minute
    const interval = setInterval(checkExpiration, 60000);
    return () => clearInterval(interval);
  }, [expiresAt, endImpersonation]);

  return (
    <ImpersonationContext.Provider value={{
      isImpersonating,
      impersonatedUser,
      impersonatingAdmin,
      impersonationToken,
      expiresAt,
      startImpersonation,
      endImpersonation
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};
